import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';

// WHICH part the phase is showing. One at a time: a part is a table column, a row of
// photographs and a 3D viewport, and a manual has a dozen of them — stacked, the phase was
// a page you scrolled through to find the one part you came for, with a WebGL canvas
// running for every mesh on the way past.
//
// Each segment carries a MARK saying whether that part is done, because with one part on
// screen the selector is the only place that answer can live — otherwise finding what is
// still to do means opening all twelve. What "done" means is the caller's: a photograph in
// the video pipeline, a model in the Parts DB in the 3D one.
//
// The icon rather than a coloured dot: it is the same mark, on the same decision, as the
// switch further down the page that makes it true, so a segment says WHICH answer it is
// giving instead of leaving green to be learnt. Drawn in both states, faint when the part
// is not done, so the row keeps its rhythm and the gaps stay countable.
//
// Wrapped rather than slid sideways, for the same reason: a dozen parts overflowed one row,
// and the ones off the right edge were exactly the ones nobody had got to yet. A selector
// whose job is to show what is left has to show all of it.
//
// FORTY TOO: Phase 4 draws this same strip over the places you can stand in a manual, and it
// stood folded there — one line, scrolling — so most of the places were off the edge. Every
// strip shows every option now.
export type SelectorPart = {
  key: string;
  name: string;
  // Absent where the strip carries no mark at all — see PartSelector's `icon`, which is what
  // decides whether this is ever read. NULL WHERE THE ANSWER IS NOT IN YET — a spinner in the
  // mark's place, never the faint "not done" mark, which is a claim nobody has made.
  done?: boolean | null;
  // NO MARK ON THIS SEGMENT, whatever the strip carries: a tab that is a view, not a thing to finish.
  noMark?: boolean;
  // WHICH BLOCK THIS SEGMENT BELONGS TO: the tiles of one block share a width where `widthBy` is
  // 'group' (see `tiles`). Not a row break — that is `row`. Absent is a block of its
  // own — the unnamed one every part is in.
  group?: string;
  // AND THE COLOUR OF ITS MARK where the done-green is not the right green. An output's mesh is
  // not a Parts DB row, and the same tick in the same green would claim it was. Only the mark:
  // a segment is a segment, and the selected one is emerald wherever it sits.
  accent?: 'purple';
  // SHOWN AND NOT PRESSABLE, in grey: a place the strip has nothing to open yet (an assembly
  // sub-step no validated joint filled), kept in sight so the strip still says how many there are.
  disabled?: boolean;
  // GREY AND STILL PRESSABLE: a place with nothing to do in it, said by the tile itself where the
  // strip carries no mark (the sub-steps strip — a sub-step with no movement to show).
  dim?: boolean;
  // FRAMED IN GREEN: a place somebody has signed off (the sub-steps strip, a validated movement).
  // A frame and not a fill, so it shows on the chosen tile too — and a ring, which takes no room,
  // so a framed tile is the width of the others.
  signed?: boolean;
  // WHICH LINE OF THE STRIP IT BELONGS ON: a tile whose `row` differs from the one before it
  // starts a new line, full or not — the sub-steps strip, a step to a line. Absent, the strip
  // breaks only where it is full.
  row?: string;
  // WHAT THE TILE'S TOOLTIP SAYS in place of its name: why a greyed tile cannot be pressed yet.
  hint?: string;
};

// THE GAP BETWEEN TILES, in pixels — real space rather than a shared hairline. A grid whose
// only seam is a 1px border a shade off its own fill reads as one slab no matter how bright
// that seam is: the eye separates two things far more readily when the page shows through
// between them than when it has to notice a line. The row breaks need this number too, since they are
// deciding how many tiles fit a row and a gap costs space exactly like a tile does.
const GAP = 8;

// The shared look of a tile. One definition, because the row is measured by rendering the
// same tiles off-screen and reading their natural widths — measured in one style and laid
// out in another, every row would break in the wrong place. And every strip of tabs in the app
// wears it (see JointList), so a chosen tab looks chosen the same way everywhere.
//
// `dim` is the grey of a tile that is not finished yet and can still be opened; `off` is the
// same grey on one that cannot. `small` is the tight tile of a strip of many short names.
export const segCls = (on: boolean, accent?: SelectorPart['accent'], off = false, dim = false, small = false) =>
  `relative flex shrink-0 items-center justify-center gap-2 rounded-xl border ${
    small ? 'px-2 py-1.5' : 'px-3.5 py-2.5'
  } text-xs font-semibold transition-all ${
    off ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
    : on
      // A gradient in its own colour, lifted with a glow: a segment whose mark is purple is
      // chosen in purple too, so "this is chosen" and "this is an output, not a part" stay
      // one statement each.
      ? `z-10 scale-[1.03] border-transparent text-white shadow-lg ${
        accent === 'purple'
          ? 'bg-gradient-to-br from-purple-500 to-fuchsia-600 shadow-purple-900/40'
          : 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-blue-900/40'
      }`
      // Its own card, not a cell in somebody else's grid: an opaque white fill, a border and
      // a shadow, so the tile reads as a raised object sitting on the page. White rather than
      // the frosted translucent version — a tint only reads as "quiet" while the page behind
      // it is white, and this one is not.
      : dim
        ? 'border-slate-200 bg-slate-100 text-slate-400 hover:border-blue-400 hover:text-slate-600'
        : 'border-slate-300 bg-white text-slate-900 shadow-sm hover:border-blue-400 hover:bg-blue-50'
  }`;

// HOW WIDE A TILE IS, AT MOST. Every tile of a set is one width — see `tiles` — and this is the
// ceiling on it.
//
// 12.5rem, because the strip is capped at max-w-4xl (56rem) whatever the screen, which leaves
// about 836px inside the padding. Four tiles of 200px and three 8px
// gaps come to 824: four per row, with the slack of a dozen pixels. 13rem fits only three and
// spends a whole column on nothing; 10rem fits five and leaves 112px for the words, which is
// about sixteen characters — "08 – Chipboard screw" does not fit that, and a strip where the
// ORDINARY label is cut has stopped being readable to buy a column.
//
// At 200px the words get 152px, which is around twenty characters: every piece of hardware and
// every tool in a manual like this one reads whole, and the long component names are the ones
// that end in an ellipsis — which is the trade being made, and it is made in the right place.
const MAX_TILE = 200;

export function PartSelector({
  parts,
  activeKey,
  onSelect,
  icon: Icon,
  doneLabel,
  todoLabel,
  widthBy = 'strip',
  small = false,
}: {
  parts: SelectorPart[];
  activeKey: string;
  onSelect: (key: string) => void;
  // The mark, and what it means in each state — read out to a screen reader, which
  // otherwise gets a row of identical part names with an unnamed glyph in front of each.
  //
  // OPTIONAL, because not every strip is answering a question about each of its targets.
  // Phase 4 walks the PLACES you can stand in a manual — the tree, a step's table, one
  // gesture — and there is nothing about a place that is done or not done. A mark drawn on
  // all of them would be a column of identical glyphs claiming a state nobody tracks.
  icon?: LucideIcon;
  doneLabel?: string;
  todoLabel?: string;
  // WHAT A TILE'S WIDTH IS MEASURED OVER — the whole strip, or each block on its own.
  //
  // THE STRIP, normally, and that is not a default chosen for want of a better one: Phase 5's
  // blocks are components, hardware, tools and made pieces, which are all part names, and a
  // hardware block narrower than the components above it would say the two are different kinds
  // of tile when the only difference is that screws have shorter names.
  //
  // THE BLOCK where the blocks are categorically different labels. Phase 4 puts the views a
  // manual can be read in — "Tree view (complete)", "Parts holes" — above the places you can
  // stand in it, which are "1", "2AB", "3AI". One width across both makes forty tiles as wide
  // as the longest sentence in the strip, and the strip is then a page of mostly empty cards:
  // what a block of "3AI"s needs is the width of "3AI".
  widthBy?: 'strip' | 'group';
  // TIGHT TILES, for a strip of many short names (see segCls).
  small?: boolean;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState<number[]>([]);
  const [avail, setAvail] = useState(0);

  // MEASURED, not guessed. A label's width is the font's business — it changes with the
  // language, with the theme's font stack, and with a rename typed in the table — so the
  // only honest number comes from the browser having laid the segment out.
  const names = parts.map((p) => p.name).join('');
  useLayoutEffect(() => {
    const measure = () => {
      const ruler = rulerRef.current;
      const box = boxRef.current;
      if (!ruler || !box) return;
      // THE SAME WIDTHS ARE NOT NEW WIDTHS. This stored a fresh array on every reading, so an
      // observer firing with nothing changed still replaced the state and still re-rendered —
      // and a re-render can move the layout, which fires the observer again. An array can
      // never bail on its own the way a number does, so the comparison has to be written.
      //
      // Nothing had to be wrong for that to spin. A ResizeObserver fires on sub-pixel changes,
      // and a page long enough to bring the window's scrollbar in and out oscillates by
      // exactly that much — which is what the reader became when it started writing every
      // gesture out. The loop was always here; the manual got long enough to start it.
      // ROUNDED UP, from the exact width: offsetWidth rounds, and a label 87.6px wide given an
      // 87px tile is cut to "Tree vi…" by the fraction of a pixel it lost.
      const now = [...ruler.children].map((c) => Math.ceil((c as HTMLElement).getBoundingClientRect().width));
      setWidths((was) => (
        was.length === now.length && was.every((w, i) => w === now[i]) ? was : now
      ));
      // Less the panel's own padding (see the wrapper below). A row that fits by a pixel and
      // does not is a row that wraps its last segment alone, which is the thing being fixed.
      setAvail(Math.max(0, box.clientWidth - 16));
    };
    measure();
    // The container's width changes with the window and with the sidebar; the labels' width
    // changes when a webfont swaps in, which happens after the first measure and would
    // otherwise leave the breaks computed against fallback metrics.
    const ro = new ResizeObserver(measure);
    if (boxRef.current) ro.observe(boxRef.current);
    void document.fonts?.ready.then(measure).catch(() => {});
    return () => ro.disconnect();
  }, [names]);

  // CONTIGUOUS RUNS OF ONE GROUP, in the order given. Runs and not a sort: the caller has
  // already put the strip in the order it wants read, and a group is a break in that order
  // rather than a bucket to gather into.
  const runs = useMemo(() => {
    const out: number[][] = [];
    parts.forEach((p, i) => {
      const last = out[out.length - 1];
      if (last && (parts[last[0]].group ?? '') === (p.group ?? '')) last.push(i);
      else out.push([i]);
    });
    return out;
  }, [parts]);

  // Every tile measured, which is the condition for giving any of them a width at all.
  const measured = !!avail && widths.length === parts.length && widths.every(Boolean);

  // THE WIDTH EVERY TILE IN A SET GETS: the widest label in that set, or the ceiling, whichever
  // comes first. What the SET is, is `widthBy` — the whole strip, or one block of it.
  //
  // THE WIDEST AND NOT THE CEILING FLAT, because the labels these strips carry are nothing like
  // each other's. Phase 5's are part names and run past the ceiling; Phase 4's walk is "1",
  // "2AB", "3AI". Measured, a set is as wide as it needs and no wider.
  const tiles = useMemo(() => {
    const out = new Array<number>(parts.length).fill(0);
    if (!measured) return out;
    const sets = widthBy === 'group' ? runs : [parts.map((_, i) => i)];
    sets.forEach((set) => {
      const w = Math.max(...set.map((i) => widths[i]));
      // UNLESS THE STRIP IS ONE TILE WIDE, which is a phone: two capped tiles do not fit side by
      // side, so every row holds one tile and the ceiling buys nothing but a cut name in the
      // middle of an empty row. A set whose longest name was cut takes the whole row instead.
      const alone = 2 * MAX_TILE + GAP > avail;
      const capped = alone && w > MAX_TILE ? avail : Math.min(MAX_TILE, w);
      set.forEach((i) => { out[i] = capped; });
    });
    return out;
  }, [runs, widths, parts, measured, widthBy, avail]);

  // ONE FLOW, BROKEN ONLY WHERE THE STRIP IS FULL. A group no longer starts a row of its own: it
  // is a block of tiles of one width (see `tiles`), and the blocks run on one after another — a
  // row ends when the next tile would pass the strip's width, and nowhere else.
  //
  // GREEDILY: tiles have their widths, a row is as full as it is, and the short one is centred.
  //
  // Until the ruler has been read there is no width to give a tile and no count to break on, so
  // the strip WRAPS itself for a frame — the browser's own greedy breaks, at natural widths.
  // Laid out unwrapped it would be a dozen segments crushed into one line.
  const rows = useMemo(() => {
    // Never no rows at all: the strip would flicker empty on every mount.
    if (!measured) return [{ items: parts.map((_, i) => i) }];
    const out: { items: number[] }[] = [];
    let row: number[] = [];
    let used = 0;
    parts.forEach((_, i) => {
      const w = Math.min(tiles[i], avail);
      if (row.length && (used + GAP + w > avail || (parts[i].row ?? '') !== (parts[i - 1].row ?? ''))) {
        out.push({ items: row });
        row = [];
        used = 0;
      }
      used += (row.length ? GAP : 0) + w;
      row.push(i);
    });
    if (row.length) out.push({ items: row });
    return out;
  }, [tiles, avail, parts, measured]);


  // `width` is the strip's one tile width, and undefined is the RULER's tile: the measuring copy
  // has to be as wide as its words want, since that width is the thing being read.
  const segment = (p: SelectorPart, width?: number) => {
    const on = p.key === activeKey;
    return (
      <button
        key={p.key}
        type="button"
        onClick={() => onSelect(p.key)}
        disabled={p.disabled}
        aria-current={on ? 'true' : undefined}
        style={width ? { width } : undefined}
        // The whole name is still the accessible one and the title: what the tile shows is cut
        // to its width, and a reader who cannot tell two "102 – Second prepared…" apart has to
        // have somewhere to look.
        title={p.hint ?? p.name}
        className={`${segCls(on, p.accent, p.disabled, p.dim, small)}${p.signed ? ' ring-2 ring-emerald-500' : ''}`}
      >
        {/* On the filled segment the mark cannot be emerald — green on green says nothing —
            so it is white when the part is done and a washed-out white when it is not,
            which is the same faint/solid pair the unselected segments use. */}
        {p.noMark ? null : Icon && p.done === null ? (
          <Loader2
            aria-hidden
            className={`h-3 w-3 shrink-0 animate-spin ${on ? 'text-white/60' : 'text-slate-300'}`}
          />
        ) : Icon && (
          <Icon
            aria-label={p.done ? doneLabel : todoLabel}
            className={`h-3 w-3 shrink-0 ${
              on
                ? p.done ? 'text-white' : 'text-white/40'
                : p.done
                  ? p.accent === 'purple' ? 'text-purple-500' : 'text-blue-500'
                  : 'text-slate-300'
            }`}
          />
        )}
        {/* CUT TO THE TILE, with an ellipsis. `min-w-0` is what lets it be: a flex item will
            not shrink below its content without it, so the words would simply push the tile
            past the width it was given. The cap is the RULER's — it is what stops one very long
            name from setting a width for the whole strip. A real tile is cut by its own width,
            which on a phone is the whole row (see `tiles`). */}
        <span className={`min-w-0 truncate ${width ? '' : 'max-w-[10rem]'}`}>
          {/* A NAME OVER TWO LINES where it says so, with a line break in it. */}
          {p.name.includes('\n')
            ? p.name.split('\n').map((line) => <span key={line} className="block truncate">{line}</span>)
            : p.name}
        </span>
      </button>
    );
  };

  return (
    <div className="w-full">
      {/* The block keeps `flex-1`, so the width the rows are counted against is the width it
          actually has. */}
      <div className="mx-auto flex w-full max-w-4xl items-start gap-2">
        {/* A GRID OF ITS OWN TILES, not a row of pills sharing borders. The previous version
            butted the segments edge to edge and relied on a hairline a shade brighter than
            its own fill to tell them apart — a seam the eye has to notice rather than a gap
            it sees for free, and on the blueprint the whole block read as one slab. Each
            tile is a small white card now and they space themselves apart (see GAP).

            EVERY TILE THE SAME WIDTH — across the strip, or across its block where the blocks
            are different kinds of label (see `tiles` and `widthBy`) — and the rows centred
            under one another.
            They used to GROW to fill their row instead, which kept the block rectangular at the
            price of making a tile's size mean nothing: the same part was half a row wide on one
            line and a quarter on the next, and a row that ended up holding one stretched it into
            a banner. One width says the one true thing — these are the same kind of object —
            and what a row does not fill it simply leaves, evenly, on both sides.

            A row per line rather than one wrapping row, because where the lines break is
            decided here and not by the browser — where the strip is full, or where `row` changes. */}
        <div
          ref={boxRef}
          className="relative min-w-0 flex-1 rounded-2xl p-2"
        >
          {/* A gap between rows too, the same size as the one between tiles, so the grid
              reads as evenly spaced in both directions rather than as tight rows of loose
              tiles. */}
          <div className="flex flex-col gap-2">
            {rows.map((row, at) => (
              <div
                key={at}
                // CENTRED, which is the shape of every short row: a tile has its block's width and
                // does not stretch, so what a row does not fill it leaves at both ends.
                className={`flex w-full items-stretch justify-center gap-2 ${measured ? '' : 'flex-wrap'}`}
              >
                {row.items.map((i) => parts[i] && segment(parts[i], tiles[i]))}
              </div>
            ))}
          </div>
          {/* THE RULER: the same segments at their natural width, laid out where nothing can
              see them, so their widths can be read. Off the flow entirely — `absolute` with
              no size of its own — because a measuring copy that took part in the layout would
              be measuring the space it was itself taking up.

              IN A BOX OF ITS OWN, 0×0 AND CLIPPED, which is the part that is easy to miss. An
              absolutely positioned descendant still counts towards its scroll container's
              scrollable area, so laid straight into the block above — which is
              `overflow-hidden` — a 1736px ruler made a 896px control scrollable sideways. It
              never showed a scrollbar and nothing looked wrong, until something focused a
              segment: the browser then scrolled the block to reveal it, and every row slid a
              hundred pixels left, half of the first one clipped off the edge.

              An ancestor that clips is where descendant overflow STOPS propagating, so a 0×0
              clipping wrapper contributes exactly a 0×0 box and the block has nothing to
              scroll. The segments keep their natural width inside it because they are
              `shrink-0` — that is what makes the ruler a ruler and not another layout. */}
            <div
              aria-hidden
              className="pointer-events-none invisible absolute left-0 top-0 h-0 w-0 overflow-hidden"
            >
              <div ref={rulerRef} className="flex w-max flex-nowrap">
                {parts.map((p) => segment(p))}
              </div>
            </div>
        </div>
      </div>
      {/* Closes the selector off from what it opens. The segments wrap onto two rows on a
          manual with a dozen parts, and without this the second row and the table under it
          read as one block. */}
    </div>
  );
}
